import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useCallback, useEffect, useRef } from "preact/hooks";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { useSkinStore, resolveSpineAsset } from "../../../store/skinStore";
import { applyParallaxStep, startParallaxAnimation } from "./useSpineHelpers";

interface UseSpineSpaceProps {
  multiplier: number;
  gameState?: GameState;
}

const SPACE_ANIMATION_START_MULTIPLIER = 60;

export function useSpineSpace({ multiplier, gameState }: UseSpineSpaceProps) {
  const { getSkin } = useSkinStore();
  const spaceSpineRef = useRef<Spine | null>(null);
  const basePositionYRef = useRef<number | null>(null);
  const interpolatedMultiplierRef = useRef<number>(1);
  const prevMultiplierRef = useRef<number>(1);
  const targetMultiplierRef = useRef<number>(1);
  const tStartRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isAnimatingRef = useRef<boolean>(false);
  const hasSpaceAnimationStartedRef = useRef<boolean>(false);

  const parallaxRefs = {
    spineRef: spaceSpineRef,
    basePositionYRef,
    interpolatedMultiplierRef,
    prevMultiplierRef,
    targetMultiplierRef,
    tStartRef,
    animationFrameRef,
    isAnimatingRef,
  };

  const fitAndPositionSpace = useCallback(
    (spine: Spine, _w: number, _h: number) => {
      if (!spine) return;

      spine.position.set(0, 0);
      basePositionYRef.current = spine.position.y;
    },
    []
  );

  const applyParallax = useCallback(() => {
    const shouldContinue = applyParallaxStep(parallaxRefs);
    if (shouldContinue) {
      animationFrameRef.current = requestAnimationFrame(applyParallax);
    }
  }, []);

  const startAnimation = useCallback(
    (targetMultiplier: number) => {
      startParallaxAnimation(parallaxRefs, applyParallax, targetMultiplier);
    },
    [applyParallax]
  );

  const startSpaceAnimationWithFade = useCallback(() => {
    const spaceSpine = spaceSpineRef.current;
    if (!spaceSpine) return;

    const { state } = spaceSpine;

    state.setEmptyAnimation(0, 0);

    const starsEntry = state.addAnimation(0, "stars", false, 0);
    if (starsEntry) {
      starsEntry.mixDuration = 0.2;
    }

    state.setAnimation(1, "space", true);
  }, []);

  const createSpace = useCallback(
    async (parent: PIXI.Container, w: number, h: number) => {
      try {
        const skin = getSkin("pioneer");
        if (!skin.space) {
          console.warn("useSpineSpace: skin does not have space configuration");
          return null;
        }
        const resolved = resolveSpineAsset(skin.space);
        const skel = resolved.skeleton;
        const atl = resolved.atlas;
        await PIXI.Assets.load([skel, atl]);
        const spaceSpine = Spine.from({
          skeleton: skel,
          atlas: atl,
          scale: 1,
          autoUpdate: true,
        });

        spaceSpine.roundPixels = false;
        spaceSpine.zIndex = 0.75;

        spaceSpineRef.current = spaceSpine;
        parent.addChild(spaceSpine);

        parent.setChildIndex(spaceSpine, 0);

        spaceSpine.state.clearTracks();
        spaceSpine.skeleton.setToSetupPose();
        spaceSpine.state.apply(spaceSpine.skeleton);

        fitAndPositionSpace(spaceSpine, w, h);

        return spaceSpine;
      } catch (error) {
        console.error("useSpineSpace: ошибка создания space спайна:", error);
        throw error;
      }
    },
    [fitAndPositionSpace, getSkin]
  );

  const resizeSpace = useCallback(
    (w: number, h: number) => {
      const spaceSpine = spaceSpineRef.current;
      if (!spaceSpine) return;

      fitAndPositionSpace(spaceSpine, w, h);
    },
    [fitAndPositionSpace]
  );

  const destroySpace = useCallback(() => {
    const spaceSpine = spaceSpineRef.current;
    if (spaceSpine) {
      hasSpaceAnimationStartedRef.current = false;
      spaceSpine.destroy();
      spaceSpineRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (gameState === GameStateEnum.COUNTDOWN) return;
    startAnimation(multiplier);
  }, [multiplier, startAnimation, gameState]);

  useEffect(() => {
    if (
      multiplier >= SPACE_ANIMATION_START_MULTIPLIER &&
      !hasSpaceAnimationStartedRef.current
    ) {
      hasSpaceAnimationStartedRef.current = true;
      startSpaceAnimationWithFade();
    }
  }, [multiplier, startSpaceAnimationWithFade]);

  useEffect(() => {
    const spine = spaceSpineRef.current;

    if (!spine) return;

    if (gameState === GameStateEnum.CRASHED) {
      try {
        const [track] = spine.state.tracks;
        if (track) {
          track.timeScale = 0;
        }
      } catch (error) {
        console.warn(
          "useSpineSpace: не удалось поставить на паузу анимацию stars при краше",
          error
        );
      }
    }

    if (gameState === GameStateEnum.COUNTDOWN) {
      spine.position.x = 0;
      spine.position.y = 0;
      basePositionYRef.current = spine.position.y;

      interpolatedMultiplierRef.current = 1;
      prevMultiplierRef.current = 1;
      targetMultiplierRef.current = 1;

      try {
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
        spine.state.apply(spine.skeleton);
      } catch (error) {
        console.warn(
          "useSpineSpace: не удалось сбросить анимации stars/space",
          error
        );
      }

      hasSpaceAnimationStartedRef.current = false;

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      isAnimatingRef.current = false;
    }
  }, [gameState]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    spaceSpineRef,
    createSpace,
    resizeSpace,
    destroySpace,
    fitAndPositionSpace,
  };
}
